import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { loadPageData } from "redux/actions/Request";
import SimpleTable from "views/Components/SimpleTable";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Link,
  Tab,
} from "@material-ui/core";
import TabContext from "@material-ui/lab/TabContext";
import TabList from "@material-ui/lab/TabList";
import TabPanel from "@material-ui/lab/TabPanel";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";

import { makeStyles } from "@material-ui/core/styles";
import ActionDialog from "views/Components/ActionDialog";
import AllowlistingFilter from "views/Components/Allowlisting/Filter";

import Codex from "@blumira/blu_constants";

// leaving here for example but not currently using
// import { FeatureFlags, useFlagValue } from '../../featureFlag';

const useStyles = makeStyles((theme) => ({
  showRulesSelectTitle: {
    margin: 0,
    padding: 0,
  },
  showRulesSelectItem: {
    marginTop: "20px !important",
    marginRight: 10,
    minWidth: 240,
    backgroundColor: theme.palette.background.paper,
  },
  dialogHeaderContainer: {
    display: "flex",
    paddingRight: 30,
    alignItems: "center",
    justifyContent: "space-between",
  },
  close: {
    cursor: "pointer",
  },
  dialogTable: {
    marginLeft: 30,
    marginRight: 30,
    marginTop: 30,
    "& td": {
      paddingBottom: 15,
      verticalAlign: "top",
    },
  },
  tableRowLabel: {
    fontWeight: "bold",
    paddingRight: 20,
    whiteSpace: "nowrap",
  },
  defaultStateDescrip: {
    display: "block",
    marginTop: 10,
  },
  priorityContainer: {
    display: "flex",
    alignItems: "center",
  },
  priorityCircle: {
    width: 15,
    height: 15,
    marginRight: 5,
    borderRadius: "50%",
  },
  filtersContainer: {
    paddingBottom: 40,
  },
  buttonWrap: {
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingTop: 20,
    paddingRight: 20,
    position: "absolute",
    bottom: 0,
    left: 0,
    paddingBottom: 20,
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  tabs: {
    marginLeft: 30,
    marginRight: 30,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabPanel: {
    paddingBottom: 30,
    maxHeight: 550,
    overflow: "scroll",
    minHeight: 550,
  },
}));

const isRuleEnabled = (rule) => rule.status === 1 || rule.status === 101; //1=enabled, 101=pending_enabled

const RulesView = (props) => {
  // leaving here for example but not currently using
  // const isDetectionFilterManagementEnabled = useFlagValue(FeatureFlags.detectionFilterManagement)
  const classes = useStyles();

  const [rules, setRules] = useState([]);
  const [showRulesDropdownVal, setShowRulesDropdownVal] = useState("enabled");
  const [open, setOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [tabValue, setTabValue] = useState("0");
  const [tableExtras, setTableExtras] = useState({});

  useEffect(() => {
    setIsLoading(true);
    const _tableExtras = {};
    if (
      props.rules?.length &&
      props.conditions?.length &&
      props.actions?.length &&
      props.findingTypes?.length
    ) {
      let rulesPlus = [];
      const thisOrgsRules = props.rules.filter((r) => r.orgId === props.orgId);
      const promises = thisOrgsRules.map((rule) => {
        const thisCondition = props.conditions?.filter(
          (c) => c.id === rule.conditionSet[0].condition
        )[0];
        const thisAction = props.actions?.filter(
          (a) => a.id === thisCondition?.targetAction
        )[0];
        const thisCategory = props.findingTypes?.filter(
          (t) => t.id === thisAction?.findingType
        )[0];
        const dataTypes = thisCondition?.tags?.filter(
          (t) => t.substring(0, 5) === "type:"
        );
        _tableExtras[rule.id] = {
          fullAnalysisSummary: thisCondition?.analysis,
          priority: thisAction?.priority,
          category: thisCategory?.name,
          dataType:
            dataTypes?.length && dataTypes[0]?.length
              ? props.language[dataTypes[0].replace("type:", "")]?.display_name
              : "Multi-Source",
          defaultState: thisCondition?.tags?.some(
            (t) => t === "status:default_disabled"
          )
            ? "Disabled"
            : "Enabled",
          detectionType:
            thisCondition?.method === 500 ? "Real-Time" : "Windowed",
          allowlist: props.allowlistEntry.filter((f) => f.ruleId === rule.id),
        };
        return rulesPlus.push(rule);
      });
      Promise.all(promises).then(() => {
        setTableExtras(_tableExtras);
        setRules(rulesPlus);
        filterRules(showRulesDropdownVal);
        setIsLoading(false);
      });
    } else if (props.ready && !props.rules.length)
      // loading finished and no rules
      setIsLoading(false);
  }, [
    props.ready,
    props.rules,
    props.conditions,
    props.actions,
    props.findingTypes,
    props.allowlistEntry,
  ]);

  const replacePre = (html) => {
    return html.replaceAll("<pre>", "<code>").replaceAll("</pre>", "</code>");
  };

  const stripHTML = (html) => {
    let doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const getAnalysis = (column, model, getFullSummary) => {
    const maxChars = 256;
    if (tableExtras[model.id]?.fullAnalysisSummary) {
      if (getFullSummary) {
        return replacePre(tableExtras[model.id]?.fullAnalysisSummary);
      } else {
        const stripped = stripHTML(tableExtras[model.id]?.fullAnalysisSummary);
        return stripped.length > maxChars
          ? stripped.substring(0, maxChars).trim() + "..."
          : stripped;
      }
    } else {
      return "";
    }
  };

  const getPriority = (column, model) => {
    const priority = tableExtras[model.id]?.priority;
    let color;
    switch (priority) {
      case 1:
        color = "red";
        break;
      case 2:
        color = "#FDE03A";
        break;
      case 3:
        color = "gray";
        break;
      default:
        color = "gray";
        break;
    }

    return (
      <span className={classes.priorityContainer}>
        <div
          className={classes.priorityCircle}
          style={{ backgroundColor: color }}
        />
        P{priority}
      </span>
    );
  };

  const getPriorityIconColor = (model) => {
    const priority = model?.priority;

    switch (priority) {
      case 1:
        return "red";
      case 2:
        return "#FDE03A";
      case 3:
        return "gray";
      default:
        return "gray";
    }
  };

  const getColumns = () => {
    let columns;

    // leaving here for example but not currently using
    // if(isDetectionFilterManagementEnabled){
    if (
      !props.license.isTagged("limited-to-cloud") &&
      !props.license.isTagged("limited-to-microsoft")
    ) {
      columns = [
        {
          title: "Rule name",
          field: "name",
          css: { width: "20%" },
          searchable: true,
        },
        {
          title: "Category",
          field: "category",
          css: { width: "120px" },
          searchable: true,
          renderValue: (column, model) => tableExtras[model.id]?.category,
        },
        {
          title: "Priority",
          field: "priority",
          css: { width: "120px" },
          searchable: false,
          renderValue: (column, model) => `P${tableExtras[model.id]?.priority}`,
          icon: (model) => (
            <FiberManualRecordIcon
              style={{ color: getPriorityIconColor(tableExtras[model.id]) }}
            />
          ),
        },
        {
          title: "Data type",
          field: "dataType",
          css: { width: "160px" },
          searchable: true,
          renderValue: (column, model) => tableExtras[model.id]?.[column.field],
        },
        {
          title: "Detection type",
          field: "detectionType",
          css: { width: "160px" },
          searchable: true,
          renderValue: (column, model) => tableExtras[model.id]?.[column.field],
        },
        {
          title: "Analysis summary",
          field: "fullAnalysisSummary",
          searchable: true,
          renderValue: getAnalysis,
        },
        {
          title: "Filters",
          field: "filters",
          css: { width: "120px" },
          searchable: true,
          renderValue: (column, model) =>
            tableExtras[model.id]?.allowlist.length,
        },
      ];
    } else {
      columns = [
        {
          title: "Rule name",
          field: "name",
          css: { width: "20%" },
          searchable: true,
        },
        {
          title: "Category",
          field: "category",
          css: { width: "120px" },
          searchable: true,
          renderValue: (column, model) => tableExtras[model.id]?.category,
        },
        {
          title: "Priority",
          field: "priority",
          css: { width: "120px" },
          searchable: false,
          renderValue: (column, model) => `P${tableExtras[model.id]?.priority}`,
          icon: (model) => (
            <FiberManualRecordIcon
              style={{ color: getPriorityIconColor(tableExtras[model.id]) }}
            />
          ),
        },
        {
          title: "Data Type",
          field: "dataType",
          css: { width: "160px" },
          searchable: true,
          renderValue: (column, model) => tableExtras[model.id]?.[column.field],
        },
        {
          title: "Detection type",
          field: "detectionType",
          css: { width: "160px" },
          searchable: true,
          renderValue: (column, model) => tableExtras[model.id]?.[column.field],
        },
        {
          title: "Analysis summary",
          field: "fullAnalysisSummary",
          searchable: true,
          renderValue: getAnalysis,
        },
      ];
    }
    return columns;
  };

  const filterRules = (rulesFilter) => {
    setShowRulesDropdownVal(rulesFilter);
    let filteredRules;
    switch (rulesFilter) {
      case "enabled":
        filteredRules = props.rules.filter(
          (r) => r.orgId === props.orgId && r.status === 1
        );
        break;
      case "disabled":
        filteredRules = props.rules.filter(
          (r) => r.orgId === props.orgId && r.status !== 1
        );
        break;
      default:
        filteredRules = props.rules.filter((r) => r.orgId === props.orgId);
        break;
    }
    setRules(filteredRules);
  };

  const handleRulesModal = (contextMenu) => {
    //model = contextMenu.model if "View details" is clicked from the context menu
    //model = just contextMenu if triggering directly from row click,
    //which happens when/if current user does not have permissions to enable/disable rule and therefore, no context menu shows
    //since currently, the only 2 things in the menu are enable/disable or "View details"
    const model = contextMenu.model || contextMenu;
    setSelectedRule(model);
    setOpen(true);
  };

  const handleClose = (action) => setOpen(false);

  const handleDisable = (theSelectedRule) => {
    theSelectedRule.set({ status: 0 });
    theSelectedRule.update().then((obj) => {
      obj.set({ status: 0 }, false);
      setSelectedRule(obj);
    });
  };

  const handleEnable = (theSelectedRule) => {
    theSelectedRule.set({ status: 1 });
    theSelectedRule.update().then((obj) => {
      obj.set({ status: 1 }, false);
      setSelectedRule(obj);
    });
  };

  const handleSwitchToggle = (obj) => {
    const rule = obj instanceof rules[0].constructor ? obj : obj.model;
    setSelectedRule(rule);
    setConfirmationOpen(true);
  };

  const handleConfirmation = () => {
    if (isRuleEnabled(selectedRule)) {
      handleDisable(selectedRule);
    } else {
      handleEnable(selectedRule);
    }
    setConfirmationOpen(false);
  };

  const handleTabChange = (event, newTabValue) => {
    setTabValue(newTabValue);
  };

  return (
    <div>
      <ActionDialog
        open={confirmationOpen}
        title={`${
          isRuleEnabled(selectedRule)
            ? "Confirm detection rule change"
            : "Are you sure?"
        }`}
        description={
          isRuleEnabled(selectedRule) ? ( //confirm disable
            <span>Are you sure you want to disable this detection rule?</span>
          ) : //else confirm enable
          selectedRule.defaultState === "Disabled" ? (
            <span>
              Blumira's incident detection team has disabled this detection rule
              by default because it is likely to produce noisy findings.
            </span>
          ) : (
            <span>Are you sure you want to enable this detection rule?</span>
          )
        }
        actions={[
          {
            title: "Cancel",
            action: () => setConfirmationOpen(false),
          },
          {
            title: `Yes, ${
              isRuleEnabled(selectedRule) ? "disable" : "enable"
            } it`,
            action: handleConfirmation,
            variant: "contained",
            datacy: "rulesConfirmChangeBtn",
          },
        ]}
      />
      {/*
        Only show modal with multiple tabs (Details & Filters) to a user if they are currently in an org with an advanced license
        or if they are a Blumira superadmin.
      */}
      {props.superadmin ||
      (!props.license.isTagged("limited-to-cloud") &&
        !props.license.isTagged("limited-to-microsoft")) ? (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth={"md"}
          fullWidth={true}
        >
          <div className={classes.dialogHeaderContainer}>
            <DialogTitle>Detection Rule</DialogTitle>
            <CloseIcon
              className={classes.close}
              onClick={handleClose}
              datacy="rulesDialogCloseBtn"
            />
          </div>
          <TabContext value={tabValue}>
            <TabList
              onChange={handleTabChange}
              className={classes.tabs}
              aria-label="Detection rules details and filters"
              indicatorColor="primary"
            >
              <Tab label="Details" value="0" />
              <Tab
                label={`Filters (${
                  tableExtras[selectedRule.id]?.allowlist.length
                })`}
                value="1"
              />
            </TabList>
            <TabPanel value="0" className={classes.tabPanel}>
              <table className={classes.dialogTable}>
                <tbody>
                  <tr>
                    <td className={classes.tableRowLabel}>Name</td>
                    <td>{selectedRule.name}</td>
                  </tr>
                  {tableExtras[selectedRule.id]?.dataType !== "" && (
                    <tr>
                      <td className={classes.tableRowLabel}>Data type</td>
                      <td>{tableExtras[selectedRule.id]?.dataType}</td>
                    </tr>
                  )}
                  <tr>
                    <td className={classes.tableRowLabel}>Category</td>
                    <td>{tableExtras[selectedRule.id]?.category}</td>
                  </tr>
                  <tr>
                    <td className={classes.tableRowLabel}>Priority</td>
                    <td>{getPriority("priority", selectedRule)}</td>
                  </tr>
                  <tr>
                    <td className={classes.tableRowLabel}>Analysis summary</td>
                    <td
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: getAnalysis("summary", selectedRule, true),
                      }}
                    />
                  </tr>
                  <tr>
                    <td className={classes.tableRowLabel}>Current state</td>
                    <td>
                      {isRuleEnabled(selectedRule) ? "Enabled" : "Disabled"}
                    </td>
                  </tr>
                  <tr>
                    <td className={classes.tableRowLabel}>Default state</td>
                    <td>
                      {tableExtras[selectedRule.id]?.defaultState}
                      <span className={classes.defaultStateDescrip}>
                        {tableExtras[selectedRule.id]?.defaultState ===
                        "Enabled"
                          ? "Blumira's incident detection team has determined this rule generates findings that are likely to present a threat so it is enabled by default."
                          : "Blumira's incident detection team has determined this rule generates noisy findings that are not likely to present threats so it is disabled by default."}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </TabPanel>
            <TabPanel value="1" className={classes.tabPanel}>
              <div className={classes.filtersContainer}>
                {tableExtras[selectedRule.id]?.allowlist.length > 0
                  ? tableExtras[selectedRule.id]?.allowlist.map((model) => (
                      <AllowlistingFilter
                        key={`${model.id}-allowlist`}
                        model={model}
                        fields={model.queryParams}
                        canEdit={false}
                        users={props.users}
                        reload={props.reload}
                        closeModal={handleClose}
                      />
                    ))
                  : "No Filters"}
              </div>
              <div className={classes.buttonWrap}>
                <Link href="https://blumira.help/managingrules">
                  Get help <OpenInNewIcon />
                </Link>
              </div>
            </TabPanel>
          </TabContext>
        </Dialog>
      ) : (
        <Dialog open={open} onClose={handleClose} maxWidth={"md"}>
          <div className={classes.dialogHeaderContainer}>
            <DialogTitle>Detection rule details</DialogTitle>
            <CloseIcon
              className={classes.close}
              onClick={handleClose}
              datacy="rulesDialogCloseBtn"
            />
          </div>
          <DialogContent className={classes.dialogContent}>
            <table className={classes.dialogTable}>
              <tbody>
                <tr>
                  <td className={classes.tableRowLabel}>Name</td>
                  <td>{selectedRule.name}</td>
                </tr>
                {tableExtras[selectedRule.id]?.dataType !== "" && (
                  <tr>
                    <td className={classes.tableRowLabel}>Data type</td>
                    <td>{tableExtras[selectedRule.id]?.dataType}</td>
                  </tr>
                )}
                <tr>
                  <td className={classes.tableRowLabel}>Category</td>
                  <td>{tableExtras[selectedRule.id]?.category}</td>
                </tr>
                <tr>
                  <td className={classes.tableRowLabel}>Priority</td>
                  <td>{getPriority("priority", selectedRule)}</td>
                </tr>
                <tr>
                  <td className={classes.tableRowLabel}>Analysis summary</td>
                  <td
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: getAnalysis("summary", selectedRule, true),
                    }}
                  />
                </tr>
                <tr>
                  <td className={classes.tableRowLabel}>Current state</td>
                  <td>
                    {isRuleEnabled(selectedRule) ? "Enabled" : "Disabled"}
                  </td>
                </tr>
                <tr>
                  <td className={classes.tableRowLabel}>Default state</td>
                  <td>
                    {tableExtras[selectedRule.id]?.defaultState}
                    <span className={classes.defaultStateDescrip}>
                      {tableExtras[selectedRule.id]?.defaultState === "Enabled"
                        ? "Blumira's incident detection team has determined this rule generates findings that are likely to present a threat so it is enabled by default."
                        : "Blumira's incident detection team has determined this rule generates noisy findings that are not likely to present threats so it is disabled by default."}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </DialogContent>
        </Dialog>
      )}
      <SimpleTable
        isNorthStar
        title="Rules"
        isFetching={isLoading || !props.ready}
        columns={getColumns()}
        data={rules.filter((r) => !r.name.includes("[SAMPLE]"))} // If we ever allow this page for FREE edition, we'd want to adjust how this filter is executed
        resultsCount={0}
        rowToggleSwitches={props}
        handleSwitchToggle={handleSwitchToggle}
        handleRulesModal={handleRulesModal}
        setTabValue={setTabValue}
        emptyText={
          <span>
            Nothing to see here.
            <br />
            <br />
            NOTE: Detection rules will appear as soon as Blumira processes your
            incoming log data. This can take up to 60 minutes after you set up
            your first Cloud Connector or Sensor.
          </span>
        }
        // Only show "Filters" menu item to a user if they are currently in an org with an advanced license
        // or if they are a Blumira superadmin.
        contextMenuItems={
          props.superadmin ||
          (!props.license.isTagged("limited-to-cloud") &&
            !props.license.isTagged("limited-to-microsoft"))
            ? [
                {
                  onClick: handleRulesModal,
                  text: "View details",
                  datacy: "rulesContextMenuItemViewDetails",
                  modalTabValue: "0",
                },
                {
                  onClick: handleRulesModal,
                  text: "Filters",
                  datacy: "rulesContextMenuItemFilters",
                  modalTabValue: "1",
                },
                {
                  onClick: handleSwitchToggle,
                  text: "Enable/Disable",
                  datacy: "rulesContextMenuItemEnableDisable",
                  detectionRule: true,
                },
              ]
            : [
                {
                  onClick: handleRulesModal,
                  text: "View details",
                  datacy: "rulesContextMenuItemViewDetails",
                  modalTabValue: "0",
                },
                {
                  onClick: handleSwitchToggle,
                  text: "Enable/Disable",
                  datacy: "rulesContextMenuItemEnableDisable",
                  detectionRule: true,
                },
              ]
        }
        toolbarActions={[
          {
            isPrimary: true,
            component: (
              <FormControl className={classes.showRulesFormControl}>
                <InputLabel
                  id={"rules-select-label"}
                  className={classes.showRulesSelectTitle}
                >
                  Search preset
                </InputLabel>
                <Select
                  id={"rules-select"}
                  value={showRulesDropdownVal}
                  datacy={"searchPresetButton"}
                  labelId={"rules-select-label"}
                  className={classes.showRulesSelectItem}
                  onChange={(event) => filterRules(event.target.value)}
                >
                  <MenuItem value="all" datacy={"allRulesButton"}>
                    All detection rules
                  </MenuItem>
                  <MenuItem value="enabled">Enabled</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                </Select>
              </FormControl>
            ),
          },
        ]}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    reload: (force) => dispatch(loadPageData(force)),
  };
};

const mapStateToProps = (state, ownProps) => {
  return {
    user: state.session.settings.user,
    orgId: state.page.payload.orgId,
    language: Codex.language.models.bq.type,
    license: state.license,
    superadmin: state.session.settings.user.superadmin,
  };
};

RulesView.defaultProps = {
  ready: false,
  rules: [],
  conditions: [],
  actions: [],
  findingTypes: [],
  users: [],
  allowlistEntry: [],
  superadmin: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(RulesView);
